/**
 * pptxDeckParts.ts — read the pieces of an open `.pptx` that the transform
 * request needs: each slide's whole XML document, the deck's slide size, and
 * the theme palette that applies to a given slide.
 *
 * All of it comes from one already-in-memory copy of the package, so the deck
 * bytes are read from Office exactly once per run.
 *
 * Part lookup follows OPC relationships rather than guessing file names:
 * `ppt/slides/slide3.xml` is NOT necessarily the third slide in presentation
 * order, and a deck with several masters has more than one theme. Both are real
 * in customer decks (see the multi-master note in apply-brand/brandExtractor).
 */

import { strFromU8, unzipSync } from "fflate";
import type { SlideDimensions, SlideThemeColors } from "./slideTransformApi";

/** An unzipped OPC package: part path → raw bytes. */
export type PackageFiles = Record<string, Uint8Array>;

const PRESENTATION_PART = "ppt/presentation.xml";
const FALLBACK_THEME_PART = "ppt/theme/theme1.xml";

const REL_TYPE_SLIDE = "slide";
const REL_TYPE_SLIDE_LAYOUT = "slideLayout";
const REL_TYPE_SLIDE_MASTER = "slideMaster";
const REL_TYPE_THEME = "theme";

/** 16:9 widescreen in EMU — matches the backend's own SlideDimensions default. */
export const DEFAULT_SLIDE_DIMENSIONS: SlideDimensions = { cx: 12192000, cy: 6858000 };

/**
 * Microsoft's stock Office palette, used to fill any slot a deck's theme omits.
 * The endpoint requires all 12 colours, so a partial `<a:clrScheme>` (or a deck
 * whose theme can't be resolved) must still yield a complete palette rather
 * than a rejected request.
 */
export const DEFAULT_THEME_COLORS: SlideThemeColors = {
  dk1: "000000",
  lt1: "FFFFFF",
  dk2: "44546A",
  lt2: "E7E6E6",
  accent1: "4472C4",
  accent2: "ED7D31",
  accent3: "A5A5A5",
  accent4: "FFC000",
  accent5: "5B9BD5",
  accent6: "70AD47",
  hlink: "0563C1",
  folHlink: "954F72",
};

/** Thrown when the package is missing a part the transform cannot proceed without. */
export class DeckStructureError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DeckStructureError";
  }
}

/* ── Package + path helpers ─────────────────────────────────────── */

export function unzipDeck(deckBytes: ArrayBuffer): PackageFiles {
  return unzipSync(new Uint8Array(deckBytes));
}

export function readPartText(files: PackageFiles, partPath: string): string | null {
  const bytes = files[partPath];
  return bytes ? strFromU8(bytes) : null;
}

/** Directory of an OPC part path, without the trailing slash (`""` at the root). */
function partDirectory(partPath: string): string {
  const lastSlash = partPath.lastIndexOf("/");
  return lastSlash === -1 ? "" : partPath.slice(0, lastSlash);
}

/** The `_rels` part holding a given part's relationships. */
function relsPathFor(partPath: string): string {
  const dir = partDirectory(partPath);
  const name = partPath.slice(dir ? dir.length + 1 : 0);
  return dir ? `${dir}/_rels/${name}.rels` : `_rels/${name}.rels`;
}

/**
 * Resolve a relationship target against the owning part's directory, collapsing
 * `.` and `..` segments. A leading `/` means package-absolute.
 */
function resolveTarget(ownerPartPath: string, target: string): string {
  if (target.startsWith("/")) return target.slice(1);

  const segments = partDirectory(ownerPartPath).split("/").filter(Boolean);
  for (const segment of target.split("/")) {
    if (segment === "" || segment === ".") continue;
    if (segment === "..") segments.pop();
    else segments.push(segment);
  }
  return segments.join("/");
}

interface Relationship {
  readonly id: string;
  readonly type: string;
  readonly target: string;
}

const RELATIONSHIP_TAG_RE = /<Relationship\b[^>]*\/?>/g;
const ATTR_ID_RE = /\bId="([^"]*)"/;
const ATTR_TYPE_RE = /\bType="([^"]*)"/;
const ATTR_TARGET_RE = /\bTarget="([^"]*)"/;
const ATTR_TARGET_MODE_RE = /\bTargetMode="([^"]*)"/;

/** Parse a `.rels` part. Returns an empty list when the part is absent. */
function readRelationships(files: PackageFiles, ownerPartPath: string): Relationship[] {
  const xml = readPartText(files, relsPathFor(ownerPartPath));
  if (!xml) return [];

  const relationships: Relationship[] = [];
  for (const tag of xml.match(RELATIONSHIP_TAG_RE) ?? []) {
    // External targets (hyperlinks, linked media) are not package parts.
    if (ATTR_TARGET_MODE_RE.exec(tag)?.[1] === "External") continue;

    const id = ATTR_ID_RE.exec(tag)?.[1];
    const type = ATTR_TYPE_RE.exec(tag)?.[1];
    const target = ATTR_TARGET_RE.exec(tag)?.[1];
    if (!id || !type || !target) continue;

    // Types are namespace URIs; the last segment is the discriminating name.
    relationships.push({ id, type: type.slice(type.lastIndexOf("/") + 1), target });
  }
  return relationships;
}

/** Follow the first relationship of `relType` from `ownerPartPath` to its part path. */
function followRelationship(
  files: PackageFiles,
  ownerPartPath: string,
  relType: string,
): string | null {
  const rel = readRelationships(files, ownerPartPath).find((r) => r.type === relType);
  return rel ? resolveTarget(ownerPartPath, rel.target) : null;
}

/* ── Slide parts in presentation order ──────────────────────────── */

const SLD_ID_LST_RE = /<p:sldIdLst\b[^>]*>([\s\S]*?)<\/p:sldIdLst>/;
const SLD_ID_RE = /<p:sldId\b[^>]*\/>/g;
const R_ID_RE = /\br:id="([^"]*)"/;

/**
 * Slide part paths in presentation order (index 0 = first slide), resolved via
 * `<p:sldIdLst>` → presentation rels.
 *
 * @throws {DeckStructureError} when the presentation part or its slide list is missing.
 */
export function listSlidePartPaths(files: PackageFiles): string[] {
  const presentationXml = readPartText(files, PRESENTATION_PART);
  if (!presentationXml) {
    throw new DeckStructureError("This presentation is missing its ppt/presentation.xml part.");
  }

  const listBody = SLD_ID_LST_RE.exec(presentationXml)?.[1];
  if (listBody == null) {
    throw new DeckStructureError("This presentation has no slide list.");
  }

  const relsById = new Map(
    readRelationships(files, PRESENTATION_PART)
      .filter((r) => r.type === REL_TYPE_SLIDE)
      .map((r) => [r.id, r] as const),
  );

  const partPaths: string[] = [];
  for (const sldId of listBody.match(SLD_ID_RE) ?? []) {
    const relId = R_ID_RE.exec(sldId)?.[1];
    const rel = relId ? relsById.get(relId) : undefined;
    // A slide id with no resolvable relationship means a corrupt package; skip
    // it so the remaining slides still transform rather than failing the run.
    if (rel) partPaths.push(resolveTarget(PRESENTATION_PART, rel.target));
  }

  if (partPaths.length === 0) {
    throw new DeckStructureError("No slides could be read from this presentation.");
  }
  return partPaths;
}

/* ── Slide size ─────────────────────────────────────────────────── */

const SLD_SZ_RE = /<p:sldSz\b[^>]*>/;
const CX_RE = /\bcx="(\d+)"/;
const CY_RE = /\bcy="(\d+)"/;

/** Deck slide size in EMU; falls back to 16:9 widescreen when absent or unparseable. */
export function readSlideDimensions(files: PackageFiles): SlideDimensions {
  const presentationXml = readPartText(files, PRESENTATION_PART);
  if (!presentationXml) return DEFAULT_SLIDE_DIMENSIONS;

  const sldSz = SLD_SZ_RE.exec(presentationXml)?.[0];
  if (!sldSz) return DEFAULT_SLIDE_DIMENSIONS;

  const cx = Number(CX_RE.exec(sldSz)?.[1]);
  const cy = Number(CY_RE.exec(sldSz)?.[1]);
  return Number.isFinite(cx) && Number.isFinite(cy) && cx > 0 && cy > 0
    ? { cx, cy }
    : DEFAULT_SLIDE_DIMENSIONS;
}

/* ── Theme ──────────────────────────────────────────────────────── */

const CLR_SCHEME_RE = /<a:clrScheme\b[^>]*>([\s\S]*?)<\/a:clrScheme>/;
const SRGB_CLR_RE = /<a:srgbClr\b[^>]*\bval="([0-9A-Fa-f]{6})"/;
const SYS_CLR_RE = /<a:sysClr\b[^>]*\blastClr="([0-9A-Fa-f]{6})"/;
const MINOR_FONT_LATIN_RE = /<a:minorFont>[\s\S]*?<a:latin\b[^>]*\btypeface="([^"]*)"/;

const THEME_COLOR_SLOTS: ReadonlyArray<keyof SlideThemeColors> = [
  "dk1",
  "lt1",
  "dk2",
  "lt2",
  "accent1",
  "accent2",
  "accent3",
  "accent4",
  "accent5",
  "accent6",
  "hlink",
  "folHlink",
];

/**
 * The theme part governing a slide, resolved slide → layout → master → theme.
 * Falls back to `ppt/theme/theme1.xml` when any hop is missing.
 */
function resolveThemePartPath(files: PackageFiles, slidePartPath: string): string | null {
  const layout = followRelationship(files, slidePartPath, REL_TYPE_SLIDE_LAYOUT);
  const master = layout ? followRelationship(files, layout, REL_TYPE_SLIDE_MASTER) : null;
  const theme = master ? followRelationship(files, master, REL_TYPE_THEME) : null;
  if (theme && files[theme]) return theme;
  return files[FALLBACK_THEME_PART] ? FALLBACK_THEME_PART : null;
}

/** Pull one colour slot's hex out of a `<a:clrScheme>` body. */
function readSchemeColor(schemeBody: string, slot: keyof SlideThemeColors): string | null {
  const block = new RegExp(String.raw`<a:${slot}\b[^>]*>([\s\S]*?)</a:${slot}>`).exec(schemeBody)?.[1];
  if (!block) return null;
  // `srgbClr val="RRGGBB"` (explicit) or `sysClr ... lastClr="RRGGBB"` (resolved).
  const hex = SRGB_CLR_RE.exec(block)?.[1] ?? SYS_CLR_RE.exec(block)?.[1];
  return hex ? hex.toUpperCase() : null;
}

export interface SlideTheme {
  readonly colors: SlideThemeColors;
  /** Body typeface from the theme's minor font, when the deck declares one. */
  readonly fontFamily?: string;
}

/**
 * Theme palette and body font for a slide. Every one of the 12 colour slots is
 * always populated — missing ones come from {@link DEFAULT_THEME_COLORS} — so
 * the result always satisfies the endpoint's mandatory-palette contract.
 */
export function readSlideTheme(files: PackageFiles, slidePartPath: string): SlideTheme {
  const themePath = resolveThemePartPath(files, slidePartPath);
  const themeXml = themePath ? readPartText(files, themePath) : null;
  if (!themeXml) return { colors: { ...DEFAULT_THEME_COLORS } };

  const colors: SlideThemeColors = { ...DEFAULT_THEME_COLORS };
  const schemeBody = CLR_SCHEME_RE.exec(themeXml)?.[1];
  if (schemeBody) {
    for (const slot of THEME_COLOR_SLOTS) {
      const hex = readSchemeColor(schemeBody, slot);
      if (hex) colors[slot] = hex;
    }
  }

  const fontFamily = MINOR_FONT_LATIN_RE.exec(themeXml)?.[1]?.trim();
  return fontFamily ? { colors, fontFamily } : { colors };
}
